/**
 * AdminTools.tsx — Pipeline control surface for admins.
 *
 * Rebuilt in final pass.
 *
 * Changes from original:
 *   - Dropped the inline API docs panel — there's now a dedicated public
 *     /api-docs page. AdminTools just points there.
 *   - Uses shared UI (Card, Button, Tag, Table) instead of hand-rolled styles.
 *   - Preserves all behavior: client multi-select, reset_unmatched toggle, job
 *     polling every 3s, recent-jobs list with "View" to re-attach to an old run.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api';
import { theme } from '../../theme';
import {
  Card,
  Button,
  Tag, type TagKind,
  Icon,
  Table, type Column,
  EmptyState,
  toast,
} from '../../components/ui';

/* ═══════════════ TYPES ═══════════════ */

interface PipelineJobProgress {
  current: number;
  total: number;
  message: string | null;
  percent: number;
}

interface PipelineJobResult {
  client: string;
  deterministic_matched: number;
  fuzzy_confirmed: number;
  sent_to_review: number;
  new_outlets: number;
  registry_enriched: number;
  duration: number;
}

interface PipelineJob {
  job_id: string;
  client_names: string[];
  status: 'pending' | 'running' | 'completed' | 'failed';
  progress: PipelineJobProgress;
  result: string | null;
  error: string | null;
  created_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  duration_seconds: number | null;
}

/* ═══════════════ HELPERS ═══════════════ */

const statusTagKind = (s: string): TagKind => {
  if (s === 'completed') return 'success';
  if (s === 'failed') return 'danger';
  if (s === 'running' || s === 'pending') return 'warning';
  return 'neutral';
};

/* ═══════════════ PAGE ═══════════════ */

export default function AdminTools() {
  useEffect(() => {
    document.title = 'Admin Tools | Meridian Registry';
    return () => { document.title = 'Meridian Registry'; };
  }, []);

  return (
    <div style={{ padding: 32 }}>
      {/* Page header */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{
          fontSize: 28, fontWeight: 600,
          color: theme.text.primary,
          margin: 0,
          letterSpacing: '-0.02em',
        }}>Admin Tools</h1>
        <div aria-hidden style={{
          height: 3, width: 40,
          background: theme.brand.cyan,
          borderRadius: 2,
          boxShadow: `0 0 12px ${theme.brand.cyanGlow}`,
          marginTop: 6,
        }} />
        <div style={{
          fontSize: 11, fontWeight: 600,
          color: theme.text.tertiary,
          textTransform: 'uppercase',
          letterSpacing: '0.14em',
          marginTop: 8,
        }}>Pipeline controls and platform utilities</div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <RegistryHealthPanel />
        <PipelinePanel />
        <ApiDocsLinkPanel />
      </div>
    </div>
  );
}

/* ═══════════════ REGISTRY HEALTH PANEL ═══════════════ */

interface FieldStat { count: number; pct: number }
interface RegistryHealth {
  total_outlets: number;
  avg_enrichment_score: number;
  fields: Record<string, FieldStat>;
}
interface QueueStat {
  source: string;
  status: string;
  count: number;
  avg_attempts: number;
}

const FIELD_LABELS: Record<string, string> = {
  name: 'Canonical name',
  address: 'Canonical address',
  city: 'Canonical city',
  outlet_format: 'Outlet format',
  gps: 'GPS',
  phone: 'Phone',
  website: 'Website',
  vat_number: 'VAT number',
  owner_name: 'Owner',
  opening_hours: 'Opening hours',
};
const FIELD_ORDER = [
  'name', 'address', 'city', 'outlet_format', 'gps',
  'phone', 'website', 'vat_number', 'owner_name', 'opening_hours',
];

// Display labels for enrichment sources — used by the Trigger buttons
// and (eventually) the per-source queue status breakdown.
const SOURCE_LABELS: Record<string, string> = {
  here:             'HERE Places',
  apr:              'APR Registry',
  client_upload:    'Client Upload',
  format_inference: 'Format Inference',
};
const TRIGGERABLE_SOURCES = ['here', 'apr', 'format_inference'] as const;

function coverageColor(pct: number): string {
  if (pct >= 60) return theme.semantic.success;
  if (pct >= 30) return theme.semantic.warning;
  return theme.semantic.danger;
}

function RegistryHealthPanel() {
  const [health, setHealth] = useState<RegistryHealth | null>(null);
  const [queueStats, setQueueStats] = useState<QueueStat[]>([]);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState<string | null>(null);

  const fetchAll = () => {
    setLoading(true);
    Promise.all([
      api.get<RegistryHealth>('/api/admin/enrichment/health').then(r => setHealth(r.data)).catch(() => {}),
      api.get<{ queue: QueueStat[] }>('/api/admin/enrichment/queue')
        .then(r => setQueueStats(r.data.queue || [])).catch(() => {}),
    ]).finally(() => setLoading(false));
  };

  useEffect(() => { fetchAll(); }, []);

  const triggerEnrichment = async (source: string) => {
    setTriggering(source);
    try {
      if (source === 'format_inference') {
        const res = await api.post<{ updated: number; coverage_pct: number; total_processed: number }>(
          '/api/admin/enrichment/format-inference',
          {},
        );
        toast.success(
          'Format inference complete',
          `${res.data.updated} of ${res.data.total_processed} outlets classified (${res.data.coverage_pct}%)`,
        );
      } else {
        const res = await api.post<{ status: string; count: number }>(
          '/api/admin/enrichment/enqueue',
          { source, limit: 500 },
        );
        toast.success(
          `${SOURCE_LABELS[source] || source} enrichment enqueued`,
          `${res.data.count} outlet${res.data.count === 1 ? '' : 's'} added to the queue`,
        );
      }
      fetchAll();
    } catch {
      toast.error(`Failed to run ${SOURCE_LABELS[source] || source}`);
    } finally {
      setTriggering(null);
    }
  };

  const queueTotals = queueStats.reduce<Record<string, number>>((acc, s) => {
    acc[s.status] = (acc[s.status] || 0) + s.count;
    return acc;
  }, {});
  const queueOrder: Array<[string, string]> = [
    ['pending', theme.semantic.warning],
    ['processing', theme.brand.cyan],
    ['completed', theme.semantic.success],
    ['failed', theme.semantic.danger],
    ['skipped', theme.text.tertiary],
  ];

  return (
    <Card
      title="Registry health"
      subtitle="Field coverage across the full outlet registry, plus enrichment queue status."
      action={
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {TRIGGERABLE_SOURCES.map(source => (
            <Button
              key={source}
              variant={source === 'here' ? 'primary' : 'secondary'}
              size="sm"
              loading={triggering === source}
              disabled={triggering !== null || loading}
              leftIcon={<Icon.Zap size={12} />}
              onClick={() => triggerEnrichment(source)}
            >
              {SOURCE_LABELS[source]}
            </Button>
          ))}
        </div>
      }
    >
      {loading && !health ? (
        <div style={{ color: theme.text.tertiary, fontSize: 13 }}>Loading…</div>
      ) : !health ? (
        <EmptyState
          icon={<Icon.AlertCircle size={24} />}
          title="Health stats unavailable"
          description="Could not load field coverage."
          compact
        />
      ) : (
        <>
          {/* Average completeness */}
          <div style={{ marginBottom: 20 }}>
            <div style={{
              display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
              marginBottom: 6,
            }}>
              <span style={{
                fontSize: 11, fontWeight: 600,
                color: theme.text.tertiary,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
              }}>Average completeness</span>
              <span style={{
                fontFamily: theme.font.data,
                fontSize: 18, fontWeight: 600,
                color: theme.text.primary,
                fontVariantNumeric: 'tabular-nums',
              }}>{health.avg_enrichment_score.toFixed(1)}%</span>
            </div>
            <CoverageBar pct={health.avg_enrichment_score} />
            <div style={{
              marginTop: 6,
              fontSize: 12,
              color: theme.text.tertiary,
              fontFamily: theme.font.ui,
            }}>
              <span style={{ fontFamily: theme.font.data, color: theme.text.secondary }}>
                {health.total_outlets.toLocaleString()}
              </span>{' '}outlets in registry
            </div>
          </div>

          {/* Field coverage bars */}
          <div style={{ marginBottom: 20 }}>
            <div style={{
              fontSize: 11, fontWeight: 600,
              color: theme.text.tertiary,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              marginBottom: 10,
            }}>Field coverage</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {FIELD_ORDER.filter(k => health.fields[k]).map(k => {
                const s = health.fields[k];
                return (
                  <FieldCoverageRow
                    key={k}
                    label={FIELD_LABELS[k] || k}
                    pct={s.pct}
                    count={s.count}
                  />
                );
              })}
            </div>
          </div>

          {/* Queue status */}
          <div>
            <div style={{
              fontSize: 11, fontWeight: 600,
              color: theme.text.tertiary,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              marginBottom: 10,
            }}>Enrichment queue</div>
            {queueStats.length === 0 ? (
              <div style={{ fontSize: 13, color: theme.text.tertiary }}>
                Queue is empty. Use the trigger buttons above to start populating it.
              </div>
            ) : (
              <div style={{
                display: 'flex', gap: 8, flexWrap: 'wrap',
              }}>
                {queueOrder.map(([status, color]) => {
                  const count = queueTotals[status] || 0;
                  if (count === 0) return null;
                  return (
                    <div key={status} style={{
                      display: 'inline-flex', alignItems: 'center', gap: 8,
                      padding: '6px 12px',
                      background: theme.surface.inset,
                      border: `1px solid ${theme.borderColor.default}`,
                      borderRadius: theme.radius.sm,
                    }}>
                      <span style={{
                        width: 8, height: 8, borderRadius: '50%',
                        background: color,
                      }} />
                      <span style={{
                        fontSize: 12,
                        color: theme.text.secondary,
                        textTransform: 'capitalize',
                      }}>{status}</span>
                      <span style={{
                        fontFamily: theme.font.data,
                        fontSize: 13, fontWeight: 600,
                        color: theme.text.primary,
                        fontVariantNumeric: 'tabular-nums',
                      }}>{count.toLocaleString()}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </Card>
  );
}

function CoverageBar({ pct }: { pct: number }) {
  const clamped = Math.max(0, Math.min(100, pct));
  const color = coverageColor(clamped);
  return (
    <div style={{
      height: 6,
      background: theme.surface.inset,
      borderRadius: 3,
      overflow: 'hidden',
    }}>
      <div style={{
        height: '100%',
        width: `${clamped}%`,
        background: color,
        borderRadius: 3,
        transition: 'width 300ms ease',
      }} />
    </div>
  );
}

function FieldCoverageRow({
  label, pct, count,
}: {
  label: string;
  pct: number;
  count: number;
}) {
  const color = coverageColor(pct);
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '140px 1fr 60px 90px',
      gap: 12,
      alignItems: 'center',
    }}>
      <span style={{ fontSize: 12, color: theme.text.secondary }}>{label}</span>
      <div style={{
        height: 6,
        background: theme.surface.inset,
        borderRadius: 3,
        overflow: 'hidden',
      }}>
        <div style={{
          height: '100%',
          width: `${Math.max(0, Math.min(100, pct))}%`,
          background: color,
          borderRadius: 3,
          transition: 'width 300ms ease',
        }} />
      </div>
      <span style={{
        fontFamily: theme.font.data,
        fontSize: 12,
        color,
        textAlign: 'right',
        fontVariantNumeric: 'tabular-nums',
      }}>{pct.toFixed(1)}%</span>
      <span style={{
        fontFamily: theme.font.data,
        fontSize: 11,
        color: theme.text.quaternary,
        textAlign: 'right',
        fontVariantNumeric: 'tabular-nums',
      }}>{count.toLocaleString()}</span>
    </div>
  );
}

/* ═══════════════ PIPELINE PANEL ═══════════════ */

function PipelinePanel() {
  const [clients, setClients] = useState<string[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [resetUnmatched, setResetUnmatched] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [job, setJob] = useState<PipelineJob | null>(null);
  const [launching, setLaunching] = useState(false);
  const [recentJobs, setRecentJobs] = useState<PipelineJob[]>([]);

  // Initial load: clients + recent jobs
  useEffect(() => {
    api.get<string[]>('/api/clients')
      .then(res => setClients(res.data || []))
      .catch(() => {});
    api.get<{ jobs: PipelineJob[] }>('/api/pipeline/jobs?limit=5')
      .then(res => setRecentJobs(res.data.jobs || []))
      .catch(() => {});
  }, []);

  // Poll the active job every 3s
  useEffect(() => {
    if (!jobId) return;
    let active = true;

    const pollOnce = () => {
      api.get<PipelineJob>(`/api/pipeline/job/${jobId}`)
        .then(res => {
          if (!active) return;
          setJob(res.data);
          if (res.data.status === 'completed' || res.data.status === 'failed') {
            api.get<{ jobs: PipelineJob[] }>('/api/pipeline/jobs?limit=5')
              .then(r => setRecentJobs(r.data.jobs || []))
              .catch(() => {});
          }
        })
        .catch(() => {});
    };

    pollOnce();
    const interval = setInterval(() => {
      if (!active) return;
      pollOnce();
      if (job?.status === 'completed' || job?.status === 'failed') clearInterval(interval);
    }, 3000);

    return () => { active = false; clearInterval(interval); };
  }, [jobId, job?.status]);

  const triggerPipeline = async () => {
    setLaunching(true);
    try {
      const res = await api.post<{ job_id: string; client_names: string[] }>(
        '/api/pipeline/run-hub',
        { clients: selected, reset_unmatched: resetUnmatched },
      );
      setJobId(res.data.job_id);
      setJob(null);
      toast.success('Pipeline started', `Processing ${selected.length} client${selected.length === 1 ? '' : 's'}`);
    } catch {
      toast.error('Failed to start pipeline');
    } finally {
      setLaunching(false);
    }
  };

  const toggleClient = (c: string) => {
    setSelected(prev => prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c]);
  };

  const isRunning = job?.status === 'pending' || job?.status === 'running';

  // Parse results — may be string or array already
  let parsedResults: PipelineJobResult[] = [];
  if (job?.status === 'completed' && job.result) {
    try {
      parsedResults = typeof job.result === 'string'
        ? JSON.parse(job.result)
        : (job.result as unknown as PipelineJobResult[]);
    } catch {
      // result malformed, leave empty
    }
  }

  return (
    <Card
      title="Hub pipeline"
      subtitle={
        <>All records are currently matched. Use <strong style={{ color: theme.text.secondary }}>Reset unmatched</strong> to reprocess records that previously couldn't be matched — useful after new registry data is added.</>
      }
    >
      {/* Client selector */}
      <div style={{ marginBottom: 20 }}>
        <SectionLabel>Select clients</SectionLabel>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {clients.map(c => {
            const active = selected.includes(c);
            return (
              <button
                key={c}
                onClick={() => toggleClient(c)}
                onMouseEnter={e => { if (!active) e.currentTarget.style.borderColor = theme.borderColor.strong; }}
                onMouseLeave={e => { if (!active) e.currentTarget.style.borderColor = theme.borderColor.default; }}
                style={{
                  padding: '5px 10px',
                  borderRadius: 6,
                  fontSize: 12, fontWeight: 500,
                  fontFamily: theme.font.ui,
                  cursor: 'pointer',
                  background: active ? theme.brand.cyanSoft : theme.surface.card,
                  border: `1px solid ${active ? theme.brand.cyan : theme.borderColor.default}`,
                  color: active ? theme.brand.cyanLight : theme.text.secondary,
                  transition: 'all 120ms ease',
                }}
              >{c}</button>
            );
          })}
        </div>

        <div style={{ display: 'flex', gap: 16, marginTop: 10 }}>
          <TextLink onClick={() => setSelected([...clients])}>Select all</TextLink>
          <TextLink onClick={() => setSelected([])} muted>Clear</TextLink>
        </div>
      </div>

      {/* Controls row */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 16,
        marginBottom: 20, flexWrap: 'wrap',
      }}>
        <label style={{
          display: 'flex', alignItems: 'center', gap: 8,
          color: theme.text.secondary,
          fontSize: 13,
          cursor: 'pointer',
          userSelect: 'none',
        }}>
          <input
            type="checkbox"
            checked={resetUnmatched}
            onChange={e => setResetUnmatched(e.target.checked)}
            style={{ accentColor: theme.brand.cyan }}
          />
          Reset unmatched records
        </label>

        <Button
          variant="primary"
          size="sm"
          loading={launching}
          disabled={selected.length === 0 || launching || isRunning}
          onClick={triggerPipeline}
          leftIcon={<Icon.Zap size={12} />}
        >
          {isRunning ? 'Running…' : launching ? 'Starting…' : 'Run Hub Pipeline'}
        </Button>
      </div>

      {/* Active job status */}
      {job && (
        <div style={{
          padding: 16,
          background: theme.surface.inset,
          border: `1px solid ${theme.borderColor.default}`,
          borderRadius: 8,
          marginBottom: 20,
        }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            marginBottom: 10,
          }}>
            <Tag kind={statusTagKind(job.status)}>{job.status.toUpperCase()}</Tag>
            {job.duration_seconds != null && (
              <span style={{
                fontSize: 12, color: theme.text.tertiary,
                fontFamily: theme.font.data,
                fontVariantNumeric: 'tabular-nums',
              }}>{job.duration_seconds}s</span>
            )}
          </div>

          {(job.status === 'running' || job.status === 'pending') && (
            <div style={{
              height: 4,
              background: theme.surface.card,
              borderRadius: 2,
              overflow: 'hidden',
              marginBottom: 8,
            }}>
              <div style={{
                height: '100%',
                width: `${job.progress.percent}%`,
                background: theme.brand.cyan,
                borderRadius: 2,
                transition: 'width 300ms ease',
                boxShadow: `0 0 8px ${theme.brand.cyanGlow}`,
              }} />
            </div>
          )}

          {job.progress.message && (
            <div style={{ fontSize: 12, color: theme.text.secondary }}>
              {job.progress.message}
            </div>
          )}
          {job.error && (
            <div style={{
              fontSize: 12,
              color: theme.semantic.danger,
              marginTop: 6,
            }}>{job.error}</div>
          )}

          {/* Results per client */}
          {parsedResults.length > 0 && (
            <div style={{ marginTop: 14, marginLeft: -16, marginRight: -16, marginBottom: -16 }}>
              <Table
                columns={resultColumns}
                data={parsedResults}
                compact
                rowKey={(_, i) => i}
              />
            </div>
          )}
        </div>
      )}

      {/* Recent jobs — hide while live job is running */}
      {recentJobs.length > 0 && !isRunning && (
        <div>
          <SectionLabel>Recent jobs</SectionLabel>
          <div style={{
            border: `1px solid ${theme.borderColor.subtle}`,
            borderRadius: 8,
            overflow: 'hidden',
          }}>
            {recentJobs.map((j, i) => (
              <div key={j.job_id} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '10px 14px',
                borderTop: i === 0 ? 'none' : `1px solid ${theme.borderColor.subtle}`,
                gap: 12,
              }}>
                <span style={{
                  flex: 1, minWidth: 0,
                  color: theme.text.secondary,
                  fontSize: 13,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>{j.client_names?.join(', ') || '—'}</span>
                <Tag kind={statusTagKind(j.status)} size="sm">{j.status}</Tag>
                <span style={{
                  fontFamily: theme.font.data,
                  fontSize: 11,
                  color: theme.text.quaternary,
                  minWidth: 70,
                  textAlign: 'right',
                  fontVariantNumeric: 'tabular-nums',
                }}>
                  {j.created_at ? new Date(j.created_at).toLocaleTimeString() : ''}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setJobId(j.job_id); setJob(null); }}
                >View</Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty state when no jobs at all */}
      {recentJobs.length === 0 && !job && !launching && (
        <EmptyState
          icon={<Icon.Clock size={24} />}
          title="No pipeline runs yet"
          description="Select clients above and trigger your first run."
          compact
        />
      )}
    </Card>
  );
}

/** Columns for the completed-job results table. */
const resultColumns: Column<PipelineJobResult>[] = [
  {
    key: 'client',
    header: 'Client',
    render: r => <span style={{ color: theme.text.primary }}>{r.client}</span>,
  },
  {
    key: 'deterministic_matched',
    header: 'Exact',
    mono: true,
    align: 'right',
    render: r => <ColoredNumber value={r.deterministic_matched} positive={theme.semantic.success} />,
  },
  {
    key: 'fuzzy_confirmed',
    header: 'Smart',
    mono: true,
    align: 'right',
    render: r => <ColoredNumber value={r.fuzzy_confirmed} positive={theme.brand.cyanLight} />,
  },
  {
    key: 'sent_to_review',
    header: 'Review',
    mono: true,
    align: 'right',
    render: r => <ColoredNumber value={r.sent_to_review} positive={theme.semantic.warning} />,
  },
  {
    key: 'new_outlets',
    header: 'New',
    mono: true,
    align: 'right',
    render: r => <span style={{ color: theme.text.primary }}>{r.new_outlets}</span>,
  },
  {
    key: 'registry_enriched',
    header: 'Enriched',
    mono: true,
    align: 'right',
    render: r => <span style={{ color: theme.text.primary }}>{r.registry_enriched}</span>,
  },
  {
    key: 'duration',
    header: 'Time',
    mono: true,
    align: 'right',
    render: r => <span style={{ color: theme.text.tertiary }}>{r.duration}s</span>,
  },
];

function ColoredNumber({ value, positive }: { value: number; positive: string }) {
  return (
    <span style={{
      color: value > 0 ? positive : theme.text.tertiary,
      fontVariantNumeric: 'tabular-nums',
    }}>{value}</span>
  );
}

/* ═══════════════ API DOCS LINK ═══════════════ */

function ApiDocsLinkPanel() {
  return (
    <Card
      title="API Documentation"
      subtitle="Integrate Census IDs directly into your ERP and reporting systems via the Meridian REST API."
      action={
        <Link to="/api-docs" style={{ textDecoration: 'none' }}>
          <Button
            variant="secondary"
            size="sm"
            rightIcon={<Icon.ExternalLink size={12} />}
          >View docs</Button>
        </Link>
      }
    >
      <div style={{
        fontSize: 13, color: theme.text.secondary,
        lineHeight: 1.6,
      }}>
        Full documentation covers authentication, rate limiting, endpoints, error codes, and examples. API keys can be generated on the{' '}
        <Link to="/export" style={{ color: theme.brand.cyanLight, textDecoration: 'none' }}>Export</Link>
        {' '}page under API Access.
      </div>
    </Card>
  );
}

/* ═══════════════ SHARED SUB-COMPONENTS ═══════════════ */

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      fontSize: 11, fontWeight: 600,
      color: theme.text.tertiary,
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      marginBottom: 10,
    }}>{children}</div>
  );
}

function TextLink({ children, onClick, muted }: { children: React.ReactNode; onClick: () => void; muted?: boolean }) {
  const baseColor = muted ? theme.text.tertiary : theme.brand.cyanLight;
  return (
    <button
      onClick={onClick}
      style={{
        background: 'transparent',
        border: 'none',
        padding: 0,
        color: baseColor,
        fontSize: 12, fontWeight: 500,
        fontFamily: theme.font.ui,
        cursor: 'pointer',
      }}
      onMouseEnter={e => { e.currentTarget.style.textDecoration = 'underline'; }}
      onMouseLeave={e => { e.currentTarget.style.textDecoration = 'none'; }}
    >{children}</button>
  );
}
